import {forwardRef, HTMLInputTypeAttribute, InputHTMLAttributes, ReactNode, useState} from 'react'
import {Input as InputPrimitive} from '@base-ui/react/input'
import {EyeIcon, EyeOffIcon} from '@components/ui/icon'
import {Label} from '@components/ui/label'
import {InputStatusIcon} from '@components/ui/input-status-icon'
import {InputHelpText} from '@components/ui/input-help-text'
import {cn} from '@utilities/helpers'
import {VariantProps, cva} from 'class-variance-authority'

export const inputTextVariants = cva(
    'group/input-wrapper relative flex w-full cursor-pointer items-center gap-1 rounded-lg bg-white transition-all',
    {
        variants: {
            variant: {
                primary:
                    'border border-neutral-300 has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-neutral-100 has-[input:disabled]:text-neutral-500 not-has-[input:disabled]:hover:border-primary-300 not-has-[input:disabled]:hover:shadow-[var(--shadow-xs),0px_0px_0px_4px_var(--color-primary-100)]',
                secondary:
                    'bg-neutral-100 has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-neutral-200 has-[input:disabled]:text-neutral-500 not-has-[input:disabled]:hover:shadow-[var(--shadow-xs),0px_0px_0px_4px_var(--color-neutral-50)]',
                ghost: 'border-none bg-transparent shadow-none has-[input:disabled]:cursor-not-allowed has-[input:disabled]:text-neutral-500'
            },
            size: {
                sm: 'h-10',
                md: 'h-11',
                lg: 'h-12'
            },
            shape: {
                rectangle: 'rounded-lg',
                rounded: 'rounded-[20px]'
            },
            hasError: {
                true: 'border border-danger-300',
                false: ''
            },
            visibilityToggle: {
                true: '',
                false: ''
            }
        },
        compoundVariants: [
            // hover state turns red when there's an error (primary variant only draws a hover border)
            {
                variant: 'primary',
                hasError: true,
                class: 'not-has-[input:disabled]:hover:border-danger-300 not-has-[input:disabled]:hover:shadow-[var(--shadow-xs),0px_0px_0px_4px_var(--color-danger-100)]'
            },
            {
                variant: 'secondary',
                hasError: true,
                class: 'not-has-[input:disabled]:hover:shadow-[var(--shadow-xs),0px_0px_0px_4px_var(--color-danger-100)]'
            },
            // paddings depend on size and whether the visibility toggle occupies the right edge
            {size: 'sm', visibilityToggle: false, class: 'pr-3.5 pl-3'},
            {size: 'sm', visibilityToggle: true, class: 'pl-3'},
            {size: 'md', visibilityToggle: false, class: 'px-3.5'},
            {size: 'md', visibilityToggle: true, class: 'pl-3.5'},
            {size: 'lg', visibilityToggle: false, class: 'pr-3.5 pl-4'},
            {size: 'lg', visibilityToggle: true, class: 'pl-4'}
        ],
        defaultVariants: {
            variant: 'primary',
            size: 'md',
            shape: 'rectangle',
            hasError: false,
            visibilityToggle: false
        }
    }
)
export const INPUT_TEXT_FIELD_CLASSNAME =
    'h-full w-full flex-1 border-none text-md text-neutral-900 outline-none placeholder:text-neutral-400 focus:outline-none disabled:cursor-not-allowed' as const

export type InputTextVariants = VariantProps<typeof inputTextVariants>

export interface InputTextProps extends InputHTMLAttributes<HTMLInputElement> {
    className?: string
    label?: ReactNode
    name?: string
    type: HTMLInputTypeAttribute
    startSlot?: ReactNode
    endSlot?: ReactNode
    touched?: boolean
    errorMessage?: string
    visibilityToggle?: boolean
    helpText?: string
    placeholder?: string
    inputSize?: 'sm' | 'md' | 'lg'
    wrapperClassName?: string
    shape?: 'rectangle' | 'rounded'
    variant?: 'primary' | 'secondary' | 'ghost'
}

const visibleToggleSizeClassName = {
    sm: 'py-2 px-3',
    md: 'py-2.5 px-3',
    lg: 'py-2 px-3'
} as const

export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
    (
        {
            label,
            name,
            type,
            startSlot,
            endSlot,
            touched = false,
            errorMessage,
            helpText,
            className,
            disabled,
            visibilityToggle = false,
            inputSize = 'md',
            shape = 'rectangle',
            variant = 'primary',
            wrapperClassName,
            ...rest
        },
        ref
    ) => {
        const [eyeVisible, setEyeVisible] = useState(false)

        return (
            <div className={cn('flex flex-col gap-1.5', className)}>
                {label && <Label htmlFor={name}>{label}</Label>}
                <div
                    className={cn(
                        inputTextVariants({
                            variant,
                            size: inputSize,
                            shape,
                            hasError: !!errorMessage,
                            visibilityToggle,
                            className: wrapperClassName
                        })
                    )}
                    data-slot="input-wrapper"
                >
                    {startSlot}
                    <InputPrimitive
                        ref={ref}
                        name={name}
                        type={eyeVisible ? 'text' : type}
                        disabled={disabled}
                        className={INPUT_TEXT_FIELD_CLASSNAME}
                        {...rest}
                    />

                    <InputStatusIcon touched={touched} hasError={!!errorMessage} />
                    {endSlot}
                    {visibilityToggle && (
                        <div
                            className={cn(
                                'flex items-center border-l border-neutral-300 text-neutral-700',
                                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                                visibleToggleSizeClassName[inputSize]
                            )}
                            onClick={() => !disabled && setEyeVisible(!eyeVisible)}
                        >
                            {eyeVisible ? <EyeIcon size={20} /> : <EyeOffIcon size={20} data-color />}
                        </div>
                    )}
                </div>
                <InputHelpText error={errorMessage} helpText={helpText} />
            </div>
        )
    }
)

InputText.displayName = 'InputText'
